import React, { useState, useCallback, useMemo } from 'react';
import {
  Sparkles,
  RefreshCw,
  Trash2,
  MessageSquare,
  Wand2,
  AlertCircle,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MessageList } from '@/components/chat/MessageList';
import { MessageInput } from '@/components/chat/MessageInput';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useWorkflowAssistant } from './useWorkflowAssistant';
import { useSubscription } from '@/contexts/SubscriptionContext';
import type { WorkflowPatch, WorkflowDefinitionDto } from './types';
import type { Message } from '@/types/chat';
import type { Node, Edge } from 'reactflow';

interface WorkflowAssistantPanelProps {
  workflowId: string;
  workflowName?: string;
  nodes: Node[];
  edges: Edge[];
  onWorkflowPatch: (patch: WorkflowPatch) => void;
  onClose?: () => void;
}

export const WorkflowAssistantPanel: React.FC<WorkflowAssistantPanelProps> = ({
  workflowId,
  workflowName,
  nodes,
  edges,
  onWorkflowPatch,
  onClose,
}) => {
  const [allowUpdates, setAllowUpdates] = useState(false);
  const { subscriptionId, loading: subscriptionLoading } = useSubscription();

  // Check if subscription context is available
  const isSubscriptionAvailable = !!subscriptionId;

  // Convert ReactFlow nodes/edges to WorkflowDefinitionDto
  const workflowDefinition = useMemo<WorkflowDefinitionDto>(() => {
    return {
      name: workflowName,
      nodes: nodes.map((n) => ({
        id: n.id,
        type: n.type || 'unknown',
        label: n.data?.label || n.data?.name,
        position: n.position,
        parameters: n.data?.parameters,
      })),
      edges: edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
      })),
    };
  }, [nodes, edges, workflowName]);

  const {
    messages,
    isLoading,
    isLoadingSession,
    error,
    sessionId,
    sendMessage,
    clearMessages,
    clearError,
  } = useWorkflowAssistant({
    workflowId,
    onWorkflowPatch,
  });

  // Convert our messages to the chat component's format
  const chatMessages = useMemo<Message[]>(() => {
    return messages.map((msg) => ({
      id: msg.id,
      sessionId: sessionId || '',
      role: msg.role,
      content: msg.content,
      timestamp: msg.timestamp,
      status: msg.status,
      metadata: {
        toolCalls: msg.toolCalls,
        toolResults: msg.toolResults,
      },
    }));
  }, [messages, sessionId]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      try {
        await sendMessage(content, workflowDefinition, allowUpdates);
      } catch (err) {
        toast.error('Failed to send message', {
          description: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    },
    [sendMessage, workflowDefinition, allowUpdates]
  );

  const handleNewSession = useCallback(async () => {
    await clearMessages();
    toast.success('New Session', {
      description: 'Started a new assistant session',
    });
  }, [clearMessages]);

  const handleClearChat = useCallback(async () => {
    await clearMessages();
    toast.success('Chat Cleared', {
      description: 'Conversation permanently deleted',
    });
  }, [clearMessages]);

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Workflow Assistant</h3>
            <div className="text-xs text-muted-foreground">
              {nodes.length} nodes, {edges.length} connections
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleNewSession}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>New Session</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClearChat}
                  disabled={messages.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Clear Chat</p>
              </TooltipContent>
            </Tooltip>

            {onClose && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Close</p>
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* Mode Selector - Always Visible */}
      <div className="px-3 py-2 border-b bg-muted/10">
        <div className="flex items-center gap-2 p-1 bg-muted/50 rounded-lg">
          <button
            onClick={() => setAllowUpdates(false)}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
              !allowUpdates
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Chat Only</span>
          </button>
          <button
            onClick={() => setAllowUpdates(true)}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
              allowUpdates
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
            )}
          >
            <Wand2 className="h-4 w-4" />
            <span>Build Mode</span>
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {allowUpdates
            ? 'Assistant can add, update, or remove nodes and connections'
            : 'Assistant provides guidance without modifying the workflow'}
        </p>
      </div>

      {/* Subscription Warning */}
      {!isSubscriptionAvailable && !subscriptionLoading && (
        <Alert variant="destructive" className="mx-4 mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Subscription context not available. Please ensure you're logged in and have selected a subscription.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Banner */}
      {error && (
        <div
          className="bg-destructive/10 text-destructive px-4 py-2 text-sm border-b cursor-pointer"
          onClick={clearError}
        >
          {error}
          <span className="text-xs ml-2">(Click to dismiss)</span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        {isLoadingSession ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Loading conversation...
          </div>
        ) : (
          <MessageList
            messages={chatMessages}
            isTyping={isLoading}
            welcomeMessage="Hi! I'm your Workflow Assistant. I can help you understand, build, and optimize your workflows. What would you like to know?"
          />
        )}
      </div>

      {/* Input */}
      <MessageInput
        onSend={handleSendMessage}
        disabled={isLoading}
        placeholder={
          allowUpdates
            ? 'Ask me anything or request changes...'
            : 'Ask me about your workflow...'
        }
        maxLength={2000}
        isLoading={isLoading}
      />
    </div>
  );
};

export default WorkflowAssistantPanel;
